import React, { useState, useEffect } from 'react'
import { validateDate, formatDateInput } from '../utils/inputValidation'

interface DateInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  minAge?: number
  disabled?: boolean
  helperText?: string
  error?: string
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  required = false,
  minAge = 13,
  disabled = false,
  helperText,
  error: externalError
}) => {
  const [internalError, setInternalError] = useState<string>('')
  const [touched, setTouched] = useState(false)

  // Validate on blur or when value changes
  useEffect(() => {
    if (touched && value.length > 0) {
      const validation = validateDate(value, minAge)
      if (!validation.isValid && validation.error) {
        setInternalError(validation.error)
      } else {
        setInternalError('')
      }
    }
  }, [value, touched, minAge])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    const formatted = formatDateInput(input)
    onChange(formatted)

    // Clear error when user starts typing
    if (internalError) {
      setInternalError('')
    }
  }

  const handleBlur = () => {
    setTouched(true)

    // Validate complete date
    if (value.length === 10) {
      const validation = validateDate(value, minAge)
      if (!validation.isValid && validation.error) {
        setInternalError(validation.error)
      }
    } else if (value.length > 0) {
      setInternalError('Date must be in DD/MM/YYYY format')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].includes(e.keyCode)) {
      return
    }

    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return
    }

    // Ensure that it is a number or slash and stop the keypress if not
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105) &&
      e.keyCode !== 191 // Allow forward slash
    ) {
      e.preventDefault()
    }
  }

  const displayError = externalError || internalError
  const hasError = !!displayError

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="DD/MM/YYYY"
          maxLength={10}
          disabled={disabled}
          className={`w-full px-4 py-3 border rounded-md text-lg tracking-wide focus:outline-none focus:ring-2 transition-colors ${
            hasError
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-onrecord-orange focus:border-transparent'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${label}-error` : undefined}
        />

        {/* Calendar icon hint */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      {/* Helper text or error message */}
      {displayError ? (
        <p id={`${label}-error`} className="text-sm text-red-600 flex items-start gap-1">
          <svg
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {displayError}
        </p>
      ) : helperText ? (
        <p className="text-sm text-gray-500 flex items-start gap-1">
          <svg
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          {helperText}
        </p>
      ) : null}

      {/* Format example */}
      {!value && !touched && (
        <p className="text-xs text-gray-400 italic">
          Example: 15/06/1995
        </p>
      )}
    </div>
  )
}
